import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, AtSign, Loader2, AlarmClock, FileText, Gift, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { contextLink } from "@/lib/notesRouting";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [rows, setRows] = useState([]);
  const [digest, setDigest] = useState({ today: 0, week: 0 });
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try { const { data } = await api.get("/notifications/count"); setUnread(data.unread || 0); }
    catch { /* silent */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [list, dig] = await Promise.all([
        api.get("/notifications", { params: { limit: 20 } }),
        api.get("/notifications/digest"),
      ]);
      setRows(list.data.rows || []);
      setUnread(list.data.unread || 0);
      setDigest(dig.data || { today: 0, week: 0 });
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  // Poll the count on mount + every 60s.
  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 60000);
    return () => clearInterval(t);
  }, [refreshCount]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const openNotification = async (n) => {
    try { await api.post(`/notifications/${n.notification_id}/read`); } catch { /* ignore */ }
    setRows((prev) => prev.map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    setOpen(false);
    if (n.type === "overdue_digest" || n.context?.kind === "mynotes") {
      navigate("/my-notes");
      return;
    }
    const link = contextLink(n.context);
    if (link) {
      navigate(link);
      if (n.context?.kind === "module") {
        setTimeout(() => window.dispatchEvent(new CustomEvent("mh:open-notes")), 120);
      }
    }
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
    setRows((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  const renderRow = (n) => (
    <button
      key={n.notification_id}
      data-testid={`notification-item-${n.notification_id}`}
      onClick={() => openNotification(n)}
      className={`w-full text-left px-4 py-3 border-b border-zinc-900 hover:bg-zinc-900 transition-colors flex gap-2.5 ${n.read ? "opacity-60" : ""}`}
    >
      <span className="mt-0.5 h-6 w-6 rounded-full bg-indigo-600/15 border border-indigo-600/30 flex items-center justify-center flex-shrink-0">
        {n.type === "overdue_digest" ? <AlarmClock className="h-3 w-3 text-amber-300" /> : n.type === "request" ? <FileText className="h-3 w-3 text-emerald-300" /> : n.type === "sponsorship_feed_stale" ? <AlertTriangle className="h-3 w-3 text-amber-300" /> : n.type === "sponsorship" ? <Gift className="h-3 w-3 text-emerald-300" /> : <AtSign className="h-3 w-3 text-indigo-300" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] text-zinc-200">
          {n.type === "overdue_digest"
            ? <span className="font-semibold">Notes need your attention</span>
            : n.type === "request"
              ? <><span className="font-semibold">{n.actor_name}</span> sent a PO request{n.context?.label ? <span className="text-zinc-400"> — {n.context.label}</span> : null}</>
              : n.type === "sponsorship"
                ? <><span className="font-semibold">New website sponsorship request</span>{n.context?.label ? <span className="text-zinc-400"> — {n.context.label}</span> : null}</>
              : n.type === "sponsorship_feed_stale"
                ? <span className="font-semibold text-amber-200">Website feed may be down</span>
              : <><span className="font-semibold">{n.actor_name}</span> {n.type === "assignment" ? "assigned you a note" : "mentioned you"}
                {n.context?.label ? <span className="text-zinc-400"> in {n.context.label}</span> : null}</>}
        </span>
        <span className="block text-[12px] text-zinc-500 truncate">{n.snippet}</span>
        <span className="block text-[10px] text-zinc-600 mt-0.5">{timeAgo(n.created_at)}</span>
      </span>
      {!n.read && <span className="mt-1 h-2 w-2 rounded-full bg-indigo-500 flex-shrink-0" />}
    </button>
  );

  return (
    <div className="relative" ref={boxRef}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        title="Notifications"
        className="relative flex items-center justify-center h-[34px] w-[34px] text-zinc-400 hover:text-indigo-300 border border-zinc-800 hover:border-zinc-700 rounded-lg transition-colors"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel"
          className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl z-[80] flex flex-col">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800 flex-shrink-0">
            <span className="font-[Outfit] font-bold text-sm">Notifications</span>
            <span className="flex-1" />
            {unread > 0 && (
              <button data-testid="notification-mark-all" onClick={markAll}
                className="text-[11px] text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1">
                <Check className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>
          {/* Glanceable mention digest */}
          <div data-testid="notification-digest"
            className="px-4 py-2 border-b border-zinc-800 bg-indigo-600/5 flex-shrink-0 flex items-center gap-1.5 text-[11px] text-zinc-400">
            <AtSign className="h-3 w-3 text-indigo-400" />
            <span>You were mentioned</span>
            <span className="text-indigo-300 font-semibold">{digest.today} today</span>
            <span className="text-zinc-700">·</span>
            <span className="text-zinc-300 font-medium">{digest.week} this week</span>
          </div>
          <div className="overflow-y-auto">
            {loading ? (
              <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-400" /></div>
            ) : rows.length === 0 ? (
              <p className="text-[12px] text-zinc-600 py-10 text-center">You're all caught up.</p>
            ) : (
              (() => {
                const isToday = (iso) => {
                  if (!iso) return false;
                  const d = new Date(iso); const n = new Date();
                  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
                };
                const today = rows.filter((r) => isToday(r.created_at));
                const earlier = rows.filter((r) => !isToday(r.created_at));
                const Group = ({ label, items, testId }) => items.length === 0 ? null : (
                  <div>
                    <p data-testid={testId} className="px-4 pt-3 pb-1 text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-600">{label}</p>
                    {items.map(renderRow)}
                  </div>
                );
                return (<>
                  <Group label="Today" items={today} testId="notification-group-today" />
                  <Group label="Earlier" items={earlier} testId="notification-group-earlier" />
                </>);
              })()
            )}
          </div>
        </div>
      )}
    </div>
  );
}
